import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Users, Tag, ClipboardList, FileText, Rocket, ExternalLink, Mail, CheckCircle2, ArrowRight,
} from "lucide-react";
import { COLORS } from "@/lib/api";

const REGISTRO_URL = "https://forms.office.com/r/shDQF8wzkS";
const REPORTES_URL = "https://bit.ly/ReportesClubJL";

const REQUISITOS = [
  { text: "Llenar el Reporte de Inicio.", link: REGISTRO_URL },
  { text: "Tener una reunión de bienvenida con el equipo de Clubs de Juventudes Latentes." },
  { text: "Cumplir con la documentación de consentimiento para formar parte del programa (te la haremos llegar por correo una vez que se registren)." },
  { text: "Asistir a las asambleas bimestrales de clubs." },
  { text: "Realizar al menos 5 actividades al año (incluyen juntas de club) y subir su reporte correspondiente." },
];

const ROLES = [
  "Co-Líder I (co-presidenta)",
  "Co-Líder II (co-presidenta)",
  "Líder de Tesorería",
  "Líder de Planeación",
  "Líder de Comunicación",
  "Líder de Relaciones Públicas (opcional)",
];

const CATEGORIAS = [
  { t: "Escuela Secundaria", d: "Para quienes cursan la secundaria.", c: COLORS.magenta },
  { t: "Escuela Intermedia", d: "Para quienes cursan la preparatoria.", c: COLORS.orange },
  { t: "Universidad", d: "Para quienes cursan la universidad.", c: COLORS.lime },
  { t: "Comunidad", d: "Para clubs no vinculados a una institución educativa.", c: COLORS.yellow },
];

const fade = { hidden: { opacity: 0, y: 18 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.05 } }) };

const Step = ({ num, icon: Icon, title, children }) => (
  <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="flex gap-5">
    <div className="flex flex-col items-center">
      <span className="w-11 h-11 rounded-sm flex items-center justify-center bg-brand-ink text-white font-heading font-bold flex-shrink-0">{num}</span>
      <span className="flex-1 w-px bg-[#E5E5E5] my-2" />
    </div>
    <div className="pb-10">
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-brand-magenta" />
        <h3 className="font-heading text-xl font-semibold tracking-tight text-brand-ink">{title}</h3>
      </div>
      <div className="mt-3 text-brand-ink/80 leading-relaxed space-y-3">{children}</div>
    </div>
  </motion.div>
);

const ComoAbrirClub = () => {
  return (
    <div data-testid="subpage-como-abrir-un-club">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]">
        <div className="absolute inset-0">
          <img src="/club-banner.png" alt="Clubs de Juventudes Latentes" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,17,17,0.45), rgba(17,17,17,0.82))" }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Link to="/red-de-clubs" data-testid="subpage-back" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-6">
            <ArrowLeft size={16} /> Red de Clubs
          </Link>
          <span className="inline-block w-16 h-1.5 rounded-sm mb-6" style={{ background: COLORS.magenta }} />
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[0.98]">
            Cómo abrir un club
          </h1>
          <p className="mt-5 text-lg md:text-xl text-white/85 font-medium max-w-2xl">
            Si tienes entre 13 y 24 años, este es tu momento: descubre los pasos para abrir tu club y
            convertir tus ideas en acción. ¡Tú puedes hacer que suceda!
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        {/* Requisitos */}
        <motion.section initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
          <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink">Requisitos</h2>
          <ul className="mt-6 space-y-3">
            {REQUISITOS.map((r) => (
              <li key={r.text} className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-brand-magenta flex-shrink-0 mt-0.5" />
                <span className="text-brand-ink/80">
                  {r.link ? (
                    <a href={r.link} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-magenta hover:underline">
                      {r.text}
                    </a>
                  ) : (
                    r.text
                  )}
                </span>
              </li>
            ))}
            <li className="flex items-start gap-3">
              <CheckCircle2 size={20} className="text-brand-magenta flex-shrink-0 mt-0.5" />
              <span className="text-brand-ink/80">
                Formar parte del{" "}
                <Link to="/programas/programa-de-acompanamiento" className="font-semibold text-brand-magenta hover:underline">
                  Programa de Acompañamiento a Clubs
                </Link>{" "}
                en sus ediciones anuales.
              </span>
            </li>
          </ul>
        </motion.section>

        {/* Pasos */}
        <section className="mt-16">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink mb-8">
            Pasos para iniciar tu club
          </h2>

          <Step num="1" icon={Users} title="Organízate con 4 amigxs o más">
            <p>Platiquen sobre lo que quieren lograr, hagan una breve planificación y designen roles. Recuerden: todxs son colíderes, no hay jerarquías, todxs aportan por igual. Las colíderes pueden convertirse en líderes al año siguiente, así la transición es continua.</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ROLES.map((r) => (
                <li key={r} className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: COLORS.orange }} /> {r}
                </li>
              ))}
            </ul>
          </Step>

          <Step num="2" icon={Tag} title="Identifiquen su categoría">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CATEGORIAS.map((c) => (
                <div key={c.t} className="border border-[#E5E5E5] rounded-sm p-4">
                  <span className="inline-block w-6 h-1 rounded-sm mb-2" style={{ background: c.c }} />
                  <p className="font-semibold text-brand-ink">{c.t}</p>
                  <p className="text-sm text-brand-ink/60">{c.d}</p>
                </div>
              ))}
            </div>
          </Step>

          <Step num="3" icon={ClipboardList} title="Registra tu club (Reporte de Inicio)">
            <p>Se llena una sola vez al inicio del periodo (una sola persona, que será la Líder). Deben establecer:</p>
            <ul className="space-y-1.5 text-sm">
              <li>• <span className="font-semibold text-brand-ink">Metas:</span> 3 metas ni muy específicas ni muy generales (ej. aumentar 20% el número de colaboradorxs).</li>
              <li>• <span className="font-semibold text-brand-ink">Consejo de liderazgo:</span> definir los roles del equipo.</li>
              <li>• <span className="font-semibold text-brand-ink">Miembrxs:</span> cantidad de colaboradxs hasta ese momento.</li>
            </ul>
            <a href={REGISTRO_URL} target="_blank" rel="noopener noreferrer" data-testid="registro-link" className="inline-flex items-center gap-2 bg-brand-ink text-white font-semibold px-5 py-3 rounded-sm hover:bg-brand-orange transition-colors">
              Registro Oficial Juventudes Latentes <ExternalLink size={16} />
            </a>
            <div className="border-l-4 pl-4 py-1 text-sm" style={{ borderColor: COLORS.yellow }}>
              <p className="font-semibold text-brand-ink">Nota sobre el nombre</p>
              <p className="text-brand-ink/70">No está permitido usar nombres de países, estados o ciudades (excepción: ciudades solo si el club abarca toda la ciudad). Sí puedes añadir una palabra o letras a la ciudad. Ejemplos: <em>Latentes Resistencia CDMX</em>, <em>Latentes Zacatecas RJ</em>. ¡Busca un nombre único y creativo!</p>
            </div>
          </Step>

          <Step num="4" icon={FileText} title="Llena tus reportes">
            <p><span className="font-semibold text-brand-ink">Reporte de Actividad:</span> cada vez que terminen una actividad, describan brevemente el tipo de evento, la cantidad de asistentes y una descripción.</p>
            <a href={REPORTES_URL} target="_blank" rel="noopener noreferrer" data-testid="reportes-link" className="inline-flex items-center gap-2 border border-brand-ink text-brand-ink font-semibold px-5 py-3 rounded-sm hover:bg-brand-ink hover:text-white transition-colors">
              Reportes del Club <ExternalLink size={16} />
            </a>
          </Step>

          <div className="flex gap-5">
            <span className="w-11 h-11 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: COLORS.lime }}>
              <Rocket size={20} className="text-brand-ink" strokeWidth={2.2} />
            </span>
            <div>
              <h3 className="font-heading text-xl font-semibold tracking-tight text-brand-ink">¡Manos a la obra!</h3>
              <p className="mt-2 text-brand-ink/80 leading-relaxed">Ya pueden comenzar a planear sus actividades. Sigan a más clubs de la región para conectar, inspirarse y asistir a sus actividades. ¡Comparte esta información con toda tu junta de liderazgo!</p>
            </div>
          </div>
        </section>

        {/* Contacto */}
        <div className="mt-14 bg-white border border-[#E5E5E5] rounded-sm p-6 flex items-start gap-3">
          <Mail size={20} className="text-brand-magenta flex-shrink-0 mt-0.5" />
          <p className="text-brand-ink/80">
            ¿Preguntas o dudas sobre cómo abrir y gestionar un club? Escribe a{" "}
            <a href="mailto:cristina@latentes.mx" data-testid="cristina-email" className="font-semibold text-brand-magenta hover:underline">cristina@latentes.mx</a>{" "}
            (asociada de programas).
          </p>
        </div>

        {/* CTA red de clubs */}
        <Link
          to="/red-de-clubs/directorio-de-clubs"
          data-testid="directorio-cta"
          className="mt-8 group flex items-center justify-between gap-4 bg-brand-ink rounded-sm p-8 hover:bg-brand-orange transition-colors duration-200"
        >
          <div>
            <h3 className="font-heading text-2xl font-semibold tracking-tight text-white">
              Súmate a nuestra increíble Red de Clubs
            </h3>
            <p className="mt-1 text-white/70">Consulta el directorio y conoce los clubs activos.</p>
          </div>
          <ArrowRight size={26} className="text-white flex-shrink-0 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
};

export default ComoAbrirClub;
